// Quote Builder — bottom-toolbar modals.
//
// Each function opens a self-contained modal element appended to document.body
// and removes it on close / save. Pattern mirrors place-order.ts.
//
// Source-of-truth bodies live in /var/www/kitchenos/kos-qb-v3.js — line
// references appear in each function's banner comment.

import { confirmDialog, iconMarkup, promptDialog } from "@tkos/shared-ui";
import type { QuoteStore } from "./state";
import type { PageContext } from "../../shell/types";
import type { ItemCategory, QuoteBuilderState, QuoteZone, QuoteItem } from "./types";
import { SUPPLIERS } from "./types";
import { escapeHtml, fmtAgo, newId, fmtPence, pencify } from "./formatters";
import { setActivePicker } from "./picker-selection";
import { buildBaseItem, pushItemToZone } from "./simple-pickers";

// ─── G3 Templates — shared LS helpers ─────────────────────────────────────
// Source: kos-qb-v3.js:5880 (LS key) + 6477-6506 (CRUD helpers).
// Templates are personal retailer scratch — LS-only is OK by the
// [[feedback-localstorage-rule]] (UI / personal prefs vs business data).

const TEMPLATES_KEY = "kos_qb_templates_v3";
const TEMPLATES_MAX = 20;

interface QbTemplate {
  id: string;
  name: string;
  savedAt: number;
  zones: QuoteZone[];
  supplierDiscounts: Record<string, number>;
}

function getTemplates(): QbTemplate[] {
  try {
    const raw = localStorage.getItem(TEMPLATES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed as QbTemplate[] : [];
  } catch {
    return [];
  }
}

function writeTemplates(list: QbTemplate[]): void {
  try { localStorage.setItem(TEMPLATES_KEY, JSON.stringify(list)); }
  catch { /* quota — swallow */ }
}

function saveTemplate(name: string, q: QuoteBuilderState): QbTemplate {
  const t: QbTemplate = {
    id: newId("t"),
    name,
    savedAt: Date.now(),
    zones: JSON.parse(JSON.stringify(q.zones)) as QuoteZone[],
    supplierDiscounts: { ...q.supplierDiscounts },
  };
  const list = [t, ...getTemplates()].slice(0, TEMPLATES_MAX);
  writeTemplates(list);
  return t;
}

function deleteTemplate(tid: string): void {
  writeTemplates(getTemplates().filter((x) => x.id !== tid));
}

// ─── G21 T&Cs ─────────────────────────────────────────────────────────────
// Source: kos-qb-v3.js:6718-6743 (`_qbv3EditTandC` + `_qbv3SaveTandC`).

export function openTandCsModal(store: QuoteStore, ctx: PageContext): void {
  const current = store.get().termsAndConditions ?? "";

  const overlay = document.createElement("div");
  overlay.className = "qb-modal-overlay";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.innerHTML = `
    <div class="qb-modal">
      <header class="qb-modal__head">
        <h3>Terms &amp; Conditions</h3>
        <button type="button" class="qb-modal__close" data-role="close" aria-label="Close">${iconMarkup("close", { size: "sm" })}</button>
      </header>
      <div class="qb-modal__body">
        <p class="qb-modal__hint">This text will appear on the customer-facing PDF below the quote totals.</p>
        <textarea
          class="qb-tandc-text"
          data-role="text"
          rows="10"
          spellcheck="true"
          placeholder="e.g. Quote valid for 30 days. 50% deposit required to confirm order. Balance due before installation."
        >${escapeHtml(current)}</textarea>
      </div>
      <footer class="qb-modal__foot">
        <div class="qb-modal__foot-actions">
          <button type="button" class="qb-btn qb-btn--ghost" data-role="cancel">Cancel</button>
          <button type="button" class="qb-btn qb-btn--primary" data-role="save">Save T&amp;Cs</button>
        </div>
      </footer>
    </div>
  `;

  const close = (): void => overlay.remove();
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) close();
  });
  overlay.querySelector<HTMLButtonElement>("[data-role='close']")?.addEventListener("click", close);
  overlay.querySelector<HTMLButtonElement>("[data-role='cancel']")?.addEventListener("click", close);
  overlay.querySelector<HTMLButtonElement>("[data-role='save']")?.addEventListener("click", () => {
    const ta = overlay.querySelector<HTMLTextAreaElement>("[data-role='text']");
    const value = ta ? ta.value : "";
    store.set((d) => { d.termsAndConditions = value; });
    close();
    ctx.showToast("Terms & conditions saved", "success");
  });

  document.body.appendChild(overlay);
  setTimeout(() => overlay.querySelector<HTMLTextAreaElement>("[data-role='text']")?.focus(), 0);
}

// ─── G20 Dimensions ───────────────────────────────────────────────────────
// Source: kos-qb-v3.js:6616-6653 (`_qbv3ValidateDimensions`).
// Sums cabinet width × qty per zone, bucketed by Base / Wall / Tall via
// the unitTypeId prefix.

type DimRow = { zone: string; run: "Base" | "Wall" | "Tall"; totalMm: number };

export function openDimensionsModal(store: QuoteStore, _ctx: PageContext): void {
  const q = store.get();
  const rows: DimRow[] = [];
  for (const z of q.zones) {
    let base = 0, wall = 0, tall = 0;
    for (const it of z.items) {
      if (it.category !== "cabinets") continue;
      const w = Number(it.width) || 0;
      const qty = Number(it.qty) || 1;
      const uid = (it.unitTypeId || "").toLowerCase();
      if (uid.startsWith("wall")) wall += w * qty;
      else if (uid.startsWith("tall")) tall += w * qty;
      else base += w * qty;
    }
    if (base > 0) rows.push({ zone: z.name, run: "Base", totalMm: base });
    if (wall > 0) rows.push({ zone: z.name, run: "Wall", totalMm: wall });
    if (tall > 0) rows.push({ zone: z.name, run: "Tall", totalMm: tall });
  }

  const overlay = document.createElement("div");
  overlay.className = "qb-modal-overlay";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.innerHTML = `
    <div class="qb-modal">
      <header class="qb-modal__head">
        <h3>Dimension summary</h3>
        <button type="button" class="qb-modal__close" data-role="close" aria-label="Close">${iconMarkup("close", { size: "sm" })}</button>
      </header>
      <div class="qb-modal__body">
        <p class="qb-modal__hint">Total cabinet run per zone and category. Cross-reference against your room survey.</p>
        ${rows.length === 0
          ? `<div class="qb-dims__empty">No cabinet dimensions to validate yet.</div>`
          : `<table class="qb-dims__table">
              <thead><tr><th>Zone</th><th>Run</th><th class="qb-dims__r">Total</th></tr></thead>
              <tbody>
                ${rows.map((r) => `
                  <tr>
                    <td class="qb-dims__zone">${escapeHtml(r.zone)}</td>
                    <td class="qb-dims__run">${r.run} run</td>
                    <td class="qb-dims__r qb-dims__total">${r.totalMm}mm <span class="qb-dims__m">(${(r.totalMm / 1000).toFixed(2)}m)</span></td>
                  </tr>
                `).join("")}
              </tbody>
            </table>`
        }
      </div>
      <footer class="qb-modal__foot">
        <div class="qb-modal__foot-actions">
          <button type="button" class="qb-btn qb-btn--ghost" data-role="cancel">Close</button>
        </div>
      </footer>
    </div>
  `;

  const close = (): void => overlay.remove();
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) close();
  });
  overlay.querySelector<HTMLButtonElement>("[data-role='close']")?.addEventListener("click", close);
  overlay.querySelector<HTMLButtonElement>("[data-role='cancel']")?.addEventListener("click", close);

  document.body.appendChild(overlay);
}

// ─── G19 Bulk-width ───────────────────────────────────────────────────────
// Source: kos-qb-v3.js:6999-7060 (`_qbv3BulkWidth` + `_qbv3ApplyBulkWidth`).
// Lists every cabinet line with a checkbox, takes a single new width,
// applies to all checked items, recalcs through the store.

export function openBulkWidthModal(store: QuoteStore, ctx: PageContext): void {
  const q = store.get();
  const cabLines: Array<{ id: string; desc: string; zoneName: string; width: number }> = [];
  for (const z of q.zones) {
    for (const it of z.items) {
      if (it.category !== "cabinets") continue;
      cabLines.push({
        id: it.id,
        desc: it.desc || it.code || "Cabinet",
        zoneName: z.name,
        width: Number(it.width) || 0,
      });
    }
  }
  if (cabLines.length === 0) {
    ctx.showToast("No cabinet lines in the quote", "info");
    return;
  }

  const overlay = document.createElement("div");
  overlay.className = "qb-modal-overlay";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.innerHTML = `
    <div class="qb-modal">
      <header class="qb-modal__head">
        <h3>Bulk width change</h3>
        <button type="button" class="qb-modal__close" data-role="close" aria-label="Close">${iconMarkup("close", { size: "sm" })}</button>
      </header>
      <div class="qb-modal__body">
        <div class="qb-bulkw__controls">
          <label class="qb-bulkw__label" for="qb-bulkw-new">New width (mm):</label>
          <input class="qb-bulkw__input" id="qb-bulkw-new" data-role="width" type="number" min="100" max="1200" step="10" placeholder="e.g. 600" />
          <button type="button" class="qb-btn qb-btn--ghost qb-bulkw__select-all" data-role="select-all">Select all</button>
        </div>
        <div class="qb-bulkw__list">
          ${cabLines.map((r) => `
            <label class="qb-bulkw__row">
              <input type="checkbox" data-role="line" data-id="${escapeHtml(r.id)}" />
              <span class="qb-bulkw__desc">${escapeHtml(r.desc)} <span class="qb-bulkw__zone">(${escapeHtml(r.zoneName)})</span></span>
              <span class="qb-bulkw__width">${r.width}mm</span>
            </label>
          `).join("")}
        </div>
      </div>
      <footer class="qb-modal__foot">
        <div class="qb-modal__foot-actions">
          <button type="button" class="qb-btn qb-btn--ghost" data-role="cancel">Cancel</button>
          <button type="button" class="qb-btn qb-btn--primary" data-role="apply">Apply width</button>
        </div>
      </footer>
    </div>
  `;

  const close = (): void => overlay.remove();
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) close();
  });
  overlay.querySelector<HTMLButtonElement>("[data-role='close']")?.addEventListener("click", close);
  overlay.querySelector<HTMLButtonElement>("[data-role='cancel']")?.addEventListener("click", close);
  overlay.querySelector<HTMLButtonElement>("[data-role='select-all']")?.addEventListener("click", () => {
    overlay.querySelectorAll<HTMLInputElement>("[data-role='line']").forEach((cb) => { cb.checked = true; });
  });
  overlay.querySelector<HTMLButtonElement>("[data-role='apply']")?.addEventListener("click", () => {
    const widthInput = overlay.querySelector<HTMLInputElement>("[data-role='width']");
    const newW = parseFloat(widthInput?.value ?? "");
    if (!Number.isFinite(newW) || newW <= 0) {
      ctx.showToast("Enter a positive width in mm", "error");
      return;
    }
    const selected = new Set<string>();
    overlay.querySelectorAll<HTMLInputElement>("[data-role='line']").forEach((cb) => {
      if (cb.checked && cb.dataset.id) selected.add(cb.dataset.id);
    });
    if (selected.size === 0) {
      ctx.showToast("Select at least one cabinet", "info");
      return;
    }
    let count = 0;
    store.set((d) => {
      for (const z of d.zones) {
        for (const it of z.items) {
          if (selected.has(it.id)) {
            it.width = newW;
            count++;
          }
        }
      }
    });
    close();
    ctx.showToast(`Applied ${newW}mm to ${count} cabinet${count === 1 ? "" : "s"}`, "success");
  });

  document.body.appendChild(overlay);
  setTimeout(() => overlay.querySelector<HTMLInputElement>("[data-role='width']")?.focus(), 0);
}

// ─── G5 Missing-items Checklist ───────────────────────────────────────────
// Source: kos-qb-v3.js:6656-6694 (`_qbv3MissingItems`).
// Standard 8-category checklist with pass/fail badges + per-row "+ Add"
// button that opens the picker for the missing category.

type ChecklistRow = { cat: ItemCategory; label: string; hint: string };

const CHECKLIST: ReadonlyArray<ChecklistRow> = [
  { cat: "cabinets",   label: "Cabinets",   hint: "Base, wall, and tall units" },
  { cat: "doors",      label: "Doors",      hint: "Door fronts for all cabinets" },
  { cat: "handles",    label: "Handles",    hint: "Handles or integrated handle spec" },
  { cat: "worktops",   label: "Worktops",   hint: "Worktop for all base runs" },
  { cat: "plinth",     label: "Plinth",     hint: "Plinth for all base units" },
  { cat: "panels",     label: "End panels", hint: "End panels for exposed sides" },
  { cat: "labour",     label: "Labour",     hint: "Installation / fitting charge" },
  { cat: "appliances", label: "Appliances", hint: "Oven, hob, extractor, dishwasher…" },
];

export function openChecklistModal(store: QuoteStore, ctx: PageContext): void {
  const q = store.get();
  const counts = new Map<ItemCategory, number>();
  for (const z of q.zones) {
    for (const it of z.items) {
      counts.set(it.category, (counts.get(it.category) ?? 0) + 1);
    }
  }

  const overlay = document.createElement("div");
  overlay.className = "qb-modal-overlay";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.innerHTML = `
    <div class="qb-modal">
      <header class="qb-modal__head">
        <h3>Missing items checklist</h3>
        <button type="button" class="qb-modal__close" data-role="close" aria-label="Close">${iconMarkup("close", { size: "sm" })}</button>
      </header>
      <div class="qb-modal__body">
        <div class="qb-checklist__list">
          ${CHECKLIST.map((c) => {
            const count = counts.get(c.cat) ?? 0;
            const present = count > 0;
            return `
              <div class="qb-checklist__row">
                <div class="qb-checklist__mark qb-checklist__mark--${present ? "ok" : "miss"}" aria-hidden="true">
                  ${present ? "✓" : "✗"}
                </div>
                <div class="qb-checklist__text">
                  <div class="qb-checklist__label">${escapeHtml(c.label)}</div>
                  <div class="qb-checklist__hint">${escapeHtml(c.hint)}${present ? ` · ${count} line${count === 1 ? "" : "s"}` : ""}</div>
                </div>
                ${present ? "" : `
                  <button type="button" class="qb-btn qb-btn--ghost qb-checklist__add" data-role="add" data-cat="${c.cat}">
                    + Add
                  </button>
                `}
              </div>
            `;
          }).join("")}
        </div>
      </div>
      <footer class="qb-modal__foot">
        <div class="qb-modal__foot-actions">
          <button type="button" class="qb-btn qb-btn--ghost" data-role="cancel">Close</button>
        </div>
      </footer>
    </div>
  `;

  const close = (): void => overlay.remove();
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) close();
  });
  overlay.querySelector<HTMLButtonElement>("[data-role='close']")?.addEventListener("click", close);
  overlay.querySelector<HTMLButtonElement>("[data-role='cancel']")?.addEventListener("click", close);
  overlay.querySelectorAll<HTMLButtonElement>("[data-role='add']").forEach((btn) => {
    btn.addEventListener("click", () => {
      const cat = btn.dataset.cat as ItemCategory | undefined;
      if (!cat) return;
      setActivePicker(cat);
      close();
      ctx.showToast(`Picker opened for ${cat}`, "info");
    });
  });

  document.body.appendChild(overlay);
}

// ─── G6 Customer Approval ─────────────────────────────────────────────────
// Source: kos-qb-v3.js:6746-6791 (`_qbv3RequestSignature` + `_qbv3SaveSignature`).
// Records an in-person / verbal / email / phone approval. Sets the quote
// status to "approved" but does NOT initiate a Stripe payment or modify
// commission — those flow separately through customer-portal signing.
//
// Defraud note: the status transition emits a `qb:audit` CustomEvent so
// downstream wiring (audit-log endpoint, when shipped) can record it.

const APPROVAL_METHODS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "verbal", label: "Verbal in showroom" },
  { value: "email",  label: "Email confirmation" },
  { value: "pdf",    label: "Signed PDF returned" },
  { value: "phone",  label: "Phone" },
];

export function openApprovalModal(store: QuoteStore, ctx: PageContext): void {
  const q = store.get();
  if (!q.customer) {
    ctx.showToast("Set a customer name first", "info");
    return;
  }
  const existing = q.customerSignature ?? null;
  const today = new Date().toISOString().slice(0, 10);

  const overlay = document.createElement("div");
  overlay.className = "qb-modal-overlay";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.innerHTML = `
    <div class="qb-modal">
      <header class="qb-modal__head">
        <h3>Customer approval</h3>
        <button type="button" class="qb-modal__close" data-role="close" aria-label="Close">${iconMarkup("close", { size: "sm" })}</button>
      </header>
      <div class="qb-modal__body">
        ${existing ? `
          <div class="qb-approval__existing">
            <div class="qb-approval__signed">✓ Signed by ${escapeHtml(existing.signedBy)}</div>
            <div class="qb-approval__date">Approved ${escapeHtml(existing.signedAt)} · ${escapeHtml(existing.method)}</div>
          </div>
        ` : ""}
        <p class="qb-modal__hint">Record that the customer has approved this quote. For a full digital signature workflow, send the quote PDF and note the approval date below.</p>
        <div class="qb-approval__form">
          <div class="qb-approval__row">
            <label class="qb-approval__label" for="qb-appr-name">Approved by</label>
            <input class="qb-approval__input" id="qb-appr-name" data-role="name" type="text" value="${escapeHtml(existing?.signedBy ?? q.customer)}" placeholder="Customer name" />
          </div>
          <div class="qb-approval__row">
            <label class="qb-approval__label" for="qb-appr-date">Approval date</label>
            <input class="qb-approval__input" id="qb-appr-date" data-role="date" type="date" value="${escapeHtml(existing?.signedAt ?? today)}" />
          </div>
          <div class="qb-approval__row">
            <label class="qb-approval__label" for="qb-appr-method">Method</label>
            <select class="qb-approval__input" id="qb-appr-method" data-role="method">
              ${APPROVAL_METHODS.map((m) => `
                <option value="${m.value}"${(existing?.method ?? "verbal") === m.value ? " selected" : ""}>${escapeHtml(m.label)}</option>
              `).join("")}
            </select>
          </div>
        </div>
      </div>
      <footer class="qb-modal__foot">
        <div class="qb-modal__foot-actions">
          <button type="button" class="qb-btn qb-btn--ghost" data-role="cancel">Cancel</button>
          <button type="button" class="qb-btn qb-btn--primary" data-role="record">Record approval</button>
        </div>
      </footer>
    </div>
  `;

  const close = (): void => overlay.remove();
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) close();
  });
  overlay.querySelector<HTMLButtonElement>("[data-role='close']")?.addEventListener("click", close);
  overlay.querySelector<HTMLButtonElement>("[data-role='cancel']")?.addEventListener("click", close);
  overlay.querySelector<HTMLButtonElement>("[data-role='record']")?.addEventListener("click", () => {
    const name = overlay.querySelector<HTMLInputElement>("[data-role='name']")?.value.trim() || q.customer;
    const date = overlay.querySelector<HTMLInputElement>("[data-role='date']")?.value || today;
    const method = overlay.querySelector<HTMLSelectElement>("[data-role='method']")?.value || "verbal";
    store.set((d) => {
      d.customerSignature = { signedBy: name, signedAt: date, method };
      d.status = "approved";
    });
    try {
      window.dispatchEvent(new CustomEvent("qb:audit", {
        detail: { action: "quote.approved", entityId: q.id, reference: q.reference, signedBy: name, signedAt: date, method, at: Date.now() },
      }));
    } catch {
      /* swallow */
    }
    close();
    ctx.showToast("Customer approval recorded — status set to Approved", "success");
  });

  document.body.appendChild(overlay);
  setTimeout(() => overlay.querySelector<HTMLInputElement>("[data-role='name']")?.focus(), 0);
}

// ─── G3 Templates modal ───────────────────────────────────────────────────
// Source: kos-qb-v3.js:6515-6543 (`_qbv3ShowTemplates` + `_qbv3LoadTemplate`
// + `_qbv3DelTemplate`) + 6508-6513 (`_qbv3SaveTemplate`).

export function openTemplatesModal(store: QuoteStore, ctx: PageContext): void {
  const overlay = document.createElement("div");
  overlay.className = "qb-modal-overlay";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");

  const render = (): void => {
    const templates = getTemplates();
    const body = overlay.querySelector<HTMLElement>("[data-role='body']");
    if (!body) return;
    body.innerHTML = templates.length === 0
      ? `<div class="qb-templates__empty">No templates yet — save the current quote as a template using the button below.</div>`
      : `<div class="qb-templates__list">
          ${templates.map((t) => {
            const itemCount = t.zones.reduce((s, z) => s + (z.items?.length ?? 0), 0);
            return `
              <div class="qb-templates__row" data-id="${escapeHtml(t.id)}">
                <div class="qb-templates__text">
                  <div class="qb-templates__name">${escapeHtml(t.name)}</div>
                  <div class="qb-templates__meta">${escapeHtml(fmtAgo(t.savedAt))} · ${itemCount} item${itemCount === 1 ? "" : "s"}</div>
                </div>
                <button type="button" class="qb-btn qb-btn--ghost qb-templates__load" data-role="load" data-id="${escapeHtml(t.id)}">Load</button>
                <button type="button" class="qb-btn qb-btn--ghost qb-templates__del" data-role="del" data-id="${escapeHtml(t.id)}" aria-label="Delete template">×</button>
              </div>
            `;
          }).join("")}
         </div>`;
    body.querySelectorAll<HTMLButtonElement>("[data-role='load']").forEach((btn) => {
      btn.addEventListener("click", () => {
        const tid = btn.dataset.id;
        if (!tid) return;
        const t = getTemplates().find((x) => x.id === tid);
        if (!t) return;
        void (async () => {
          if (!(await confirmDialog(
            `Load template "${t.name}"? This replaces the current quote zones.`,
            { title: "Load template", confirmLabel: "Load", destructive: true },
          ))) return;
          store.set((d) => {
            // Deep clone + new ids so zone/item identities don't collide.
            d.zones = (JSON.parse(JSON.stringify(t.zones)) as QuoteZone[]).map((z) => ({
              ...z,
              id: newId("z"),
              summary: z.summary ?? "",
              items: z.items.map((it) => ({ ...it, id: newId("i") })),
            }));
            d.supplierDiscounts = { ...d.supplierDiscounts, ...t.supplierDiscounts };
          });
          close();
          ctx.showToast(`Template "${t.name}" loaded`, "success");
        })();
      });
    });
    body.querySelectorAll<HTMLButtonElement>("[data-role='del']").forEach((btn) => {
      btn.addEventListener("click", () => {
        const tid = btn.dataset.id;
        if (!tid) return;
        deleteTemplate(tid);
        render();
      });
    });
  };

  overlay.innerHTML = `
    <div class="qb-modal">
      <header class="qb-modal__head">
        <h3>Quote templates</h3>
        <button type="button" class="qb-modal__close" data-role="close" aria-label="Close">${iconMarkup("close", { size: "sm" })}</button>
      </header>
      <div class="qb-modal__body" data-role="body"></div>
      <footer class="qb-modal__foot">
        <div class="qb-modal__foot-actions">
          <button type="button" class="qb-btn qb-btn--ghost" data-role="cancel">Cancel</button>
          <button type="button" class="qb-btn qb-btn--primary" data-role="save">Save current as template</button>
        </div>
      </footer>
    </div>
  `;

  const close = (): void => overlay.remove();
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) close();
  });
  overlay.querySelector<HTMLButtonElement>("[data-role='close']")?.addEventListener("click", close);
  overlay.querySelector<HTMLButtonElement>("[data-role='cancel']")?.addEventListener("click", close);
  overlay.querySelector<HTMLButtonElement>("[data-role='save']")?.addEventListener("click", () => {
    void (async () => {
      const q = store.get();
      const name = await promptDialog("Template name:", {
        title: "Save template",
        defaultValue: q.project || "My template",
        confirmLabel: "Save",
      });
      if (!name) return;
      saveTemplate(name, q);
      render();
      ctx.showToast("Template saved", "success");
    })();
  });

  document.body.appendChild(overlay);
  render();
}

// ─── G4 Scanner ───────────────────────────────────────────────────────────
// Source: kos-qb-v3.js:6865-6996 (`_qbv3Scanner`, `_qbv3ScanLookup`, `_qbv3ScanAdd`).
// Cross-supplier SKU lookup hitting TKC + DTN + LDL in parallel, surfacing
// matched products with a one-click "+ Add" that pushes a minimal QuoteItem
// into the first zone.

interface ScanHit {
  supplier: string;
  sku: string;
  desc: string;
  price: number;
  w: number; h: number; d: number;
  brand: string;
  category: string;
  doorRange: string;
  doorColour: string;
}

interface RawSearchRow {
  sku?: unknown; code?: unknown;
  desc?: unknown; name?: unknown;
  price?: unknown; tradePrice?: unknown;
  w?: unknown; h?: unknown; d?: unknown;
  brand?: unknown;
  category?: unknown;
  doorRange?: unknown;
  doorColour?: unknown;
}

const SCAN_CAT_MAP: Record<string, ItemCategory> = {
  door: "doors", doors: "doors",
  cabinet: "cabinets", cabinets: "cabinets",
  handle: "handles", handles: "handles",
  worktop: "worktops", worktops: "worktops",
  panel: "panels", panels: "panels",
  plinth: "plinth",
  storage: "storage",
  lighting: "lighting",
  appliance: "appliances", appliances: "appliances",
  sink: "sundries", sinks: "sundries", accessory: "sundries", accessories: "sundries", sundries: "sundries",
};

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function str(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function supplierColour(name: string): string {
  return SUPPLIERS.find((s) => s.key === name || s.supplierKey === name)?.colour ?? "#71717a";
}

function normaliseHit(supplier: string, row: RawSearchRow): ScanHit {
  return {
    supplier,
    sku:   str(row.sku) || str(row.code),
    desc:  str(row.desc) || str(row.name),
    price: num(row.price) || num(row.tradePrice),
    w: num(row.w), h: num(row.h), d: num(row.d),
    brand: str(row.brand),
    category: str(row.category),
    doorRange: str(row.doorRange),
    doorColour: str(row.doorColour),
  };
}

function resolveCategory(hit: ScanHit): ItemCategory {
  const raw = hit.category.toLowerCase();
  return SCAN_CAT_MAP[raw] ?? "sundries";
}

function buildScanItem(store: QuoteStore, hit: ScanHit): QuoteItem {
  const cat = resolveCategory(hit);
  const extras: Partial<QuoteItem> = {
    width: hit.w,
    height: hit.h,
    depth: hit.d,
  };
  if (cat === "doors") {
    extras.doorRange = hit.doorRange;
    extras.doorColour = hit.doorColour;
    extras.handing = "left";
    extras.integratedHandle = "none";
  } else if (cat === "cabinets") {
    extras.unitTypeLabel = hit.desc;
    extras.carcassFinish = "White";
    extras.edging = "Matching carcass";
    extras.hinging = "L";
  } else if (cat === "worktops") {
    extras.material = "Laminate";
    extras.length = hit.w || 3000;
    extras.thickness = 38;
  }
  return buildBaseItem(store, cat, hit.supplier || "TKC", hit.sku, hit.desc, 1, hit.price, extras);
}

export function openScannerModal(store: QuoteStore, ctx: PageContext): void {
  const overlay = document.createElement("div");
  overlay.className = "qb-modal-overlay";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.innerHTML = `
    <div class="qb-modal">
      <header class="qb-modal__head">
        <h3>Product code scanner</h3>
        <button type="button" class="qb-modal__close" data-role="close" aria-label="Close">${iconMarkup("close", { size: "sm" })}</button>
      </header>
      <div class="qb-modal__body">
        <p class="qb-modal__hint">Type or scan a product code (SKU) to look it up across TKC, DTN, and LDL catalogues and add it directly.</p>
        <div class="qb-scan__controls">
          <input class="qb-scan__input" type="text" data-role="sku" placeholder="Scan or type SKU…" autocomplete="off" />
          <button type="button" class="qb-btn qb-btn--primary" data-role="lookup">Look up</button>
        </div>
        <div class="qb-scan__results" data-role="results"></div>
      </div>
      <footer class="qb-modal__foot">
        <div class="qb-modal__foot-actions">
          <button type="button" class="qb-btn qb-btn--ghost" data-role="cancel">Close</button>
        </div>
      </footer>
    </div>
  `;

  const close = (): void => overlay.remove();
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) close();
  });
  overlay.querySelector<HTMLButtonElement>("[data-role='close']")?.addEventListener("click", close);
  overlay.querySelector<HTMLButtonElement>("[data-role='cancel']")?.addEventListener("click", close);

  const input = overlay.querySelector<HTMLInputElement>("[data-role='sku']");
  const results = overlay.querySelector<HTMLElement>("[data-role='results']");

  const renderHits = (hits: ScanHit[]): void => {
    if (!results) return;
    if (hits.length === 0) {
      results.innerHTML = `<div class="qb-scan__empty">No products found.</div>`;
      return;
    }
    results.innerHTML = hits.map((p, idx) => `
      <div class="qb-scan__row" data-idx="${idx}">
        <span class="qb-scan__pill" style="--qb-scan-colour:${supplierColour(p.supplier)}">${escapeHtml(p.supplier)}</span>
        <div class="qb-scan__text">
          <div class="qb-scan__desc">${escapeHtml(p.desc)}</div>
          <div class="qb-scan__sub">${escapeHtml(p.sku)}${p.price ? ` · ${fmtPence(pencify(p.price))}` : ""}${p.w ? ` · ${p.w}mm` : ""}${p.brand ? ` · ${escapeHtml(p.brand)}` : ""}</div>
        </div>
        <button type="button" class="qb-btn qb-btn--primary qb-scan__add" data-role="add" data-idx="${idx}">+ Add</button>
      </div>
    `).join("");
    results.querySelectorAll<HTMLButtonElement>("[data-role='add']").forEach((btn) => {
      btn.addEventListener("click", () => {
        const i = Number(btn.dataset.idx ?? "-1");
        const hit = hits[i];
        if (!hit) return;
        const item = buildScanItem(store, hit);
        const firstZone = store.get().zones[0];
        if (!firstZone) {
          ctx.showToast("No zone to add to", "error");
          return;
        }
        pushItemToZone(store, firstZone.id, item);
        close();
        ctx.showToast(`Added ${item.desc} to ${firstZone.name}`, "success");
      });
    });
  };

  const lookup = async (): Promise<void> => {
    if (!input || !results) return;
    const sku = input.value.trim();
    if (!sku) return;
    results.innerHTML = `<div class="qb-scan__loading">Searching all catalogues…</div>`;
    const qs = `?q=${encodeURIComponent(sku)}&limit=5`;
    const fetches: Array<{ supplier: string; promise: Promise<RawSearchRow[]> }> = [
      { supplier: "TKC", promise: ctx.api<{ results?: RawSearchRow[] }>(`/tkc/search${qs}`).then((d) => d.results ?? []).catch(() => [] as RawSearchRow[]) },
      { supplier: "Trade Kitchens by DTN", promise: ctx.api<{ results?: RawSearchRow[] }>(`/dtn/search${qs}`).then((d) => d.results ?? []).catch(() => [] as RawSearchRow[]) },
      { supplier: "LDL", promise: ctx.api<{ results?: RawSearchRow[] }>(`/ldl/search${qs}`).then((d) => d.results ?? []).catch(() => [] as RawSearchRow[]) },
    ];
    const lists = await Promise.all(fetches.map(async ({ supplier, promise }) => (await promise).map((row) => normaliseHit(supplier, row))));
    renderHits(lists.flat());
  };

  input?.addEventListener("keydown", (e) => {
    if (e.key === "Enter") void lookup();
  });
  overlay.querySelector<HTMLButtonElement>("[data-role='lookup']")?.addEventListener("click", () => { void lookup(); });

  document.body.appendChild(overlay);
  setTimeout(() => input?.focus(), 0);
}

